import React from 'react';
import type { FormResponse } from '@troia-v3/types';
import { toast } from 'sonner';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { FormCardAction } from './FormCard';
import { getFormStatusMeta } from './formStatusMeta';

export interface FormListItemProps {
  form: FormResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onAction: (action: FormCardAction, form: FormResponse) => void;
}

/**
 * Linha de tabela de formulário (view "Lista"). Padrão-lista travado:
 * checkbox (1ª col) + coluna multi-linha (nome+slug) + menu ••• (última col).
 */
export const FormListItem: React.FC<FormListItemProps> = ({ form, selected = false, onToggleSelect, onAction }) => {
  const status = getFormStatusMeta(form.status);
  const isActive = form.status === 'active';
  const fieldsCount = form.fields?.length || 0;

  const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString('pt-BR') : '—');

  const copyLink = () => {
    const url = `${window.location.origin}/f/${form.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => onAction('edit', form) },
    { label: 'Relatório', icon: 'bar-chart', onClick: () => onAction('report', form) },
    { label: 'Copiar link', icon: 'link', onClick: copyLink },
    { label: 'Duplicar', icon: 'copy', onClick: () => onAction('duplicate', form) },
    {
      label: isActive ? 'Desativar' : 'Ativar',
      icon: isActive ? 'toggle-left' : 'toggle-right',
      onClick: () => onAction('toggleStatus', form),
    },
    { label: 'Excluir', icon: 'trash', onClick: () => onAction('delete', form), variant: 'danger' },
  ];

  return (
    <tr
      className="border-b transition-colors"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: selected ? 'var(--theme-primary-background)' : undefined,
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`form-row-${form.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(form.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`form-check-${form.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Formulário: nome + slug */}
      <td className="px-4 py-3">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {form.name}
          </div>
          <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            /f/{form.slug}
          </div>
        </div>
      </td>

      {/* Status — StatusPill shared (F62) */}
      <td className="px-4 py-3">
        <StatusPill
          variant={status.variant}
          label={status.label}
          data-testid={`form-status-${form.id}`}
        />
      </td>

      {/* Respostas */}
      <td className="px-4 py-3 text-sm font-bold tabular-nums" style={{ color: 'var(--theme-card-text)' }}>
        {form.responsesCount}
      </td>

      {/* Campos */}
      <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>
        {fieldsCount}
      </td>

      {/* Criado em */}
      <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>
        {formatDate(form.createdAt as unknown as string)}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`form-actions-${form.id}`} />
      </td>
    </tr>
  );
};

FormListItem.displayName = 'FormListItem';
